import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Fondori — popup shown right after an item is added to cart, offering a choice between
 * viewing the cart (now full-screen on desktop, see snippets/cart-drawer.liquid) or
 * continuing to shop. Replaces the previous behavior of silently auto-opening the cart
 * drawer on every add-to-cart (the `auto-open` attribute was removed from
 * <cart-drawer-component> in snippets/cart-drawer.liquid) — punkt 6 z Poprawki_Fondori.pdf,
 * wdrożone 14 września 2026.
 *
 * Mirrors the "defer until a source modal closes" pattern already used by
 * assets/cart-drawer.js for the same underlying event, so quick-add still behaves
 * consistently (popup appears after the quick-add dialog finishes closing, not underneath it).
 *
 * 14 września 2026 (poprawka na życzenie Daniela): popup pokazuje teraz też, JAKI produkt
 * został dodany — miniatura + nazwa (+ wariant/ilość, jeśli istotne). Dane produktu biorą się
 * z `detail.items`, który `product-form-component` (assets/product-form.js) już i tak dołącza
 * do wyniku `event.promise` — to pełny, świeży odczyt koszyka z `/cart/add.js` (ten sam kształt
 * co odpowiedź AJAX Cart API: `product_title`, `image`, `variant_title`, `variant_id`, itd.),
 * więc nie trzeba żadnego dodatkowego zapytania sieciowego. Dopasowanie do konkretnej dodanej
 * pozycji odbywa się przez `event.lines[].merchandiseId` (id wariantu, który został wysłany
 * w tym konkretnym dodaniu) — patrz #findAddedItem.
 *
 * 14 września 2026 (druga poprawka na życzenie Daniela: "zrób ten popup większy, a nie takie
 * mikro pole"): popup przeprojektowany na większy, dwukolumnowy układ (patrz
 * snippets/cart-added-popup.liquid) i rozszerzony o cenę dodanej pozycji oraz krótkie
 * podsumowanie całego koszyka (liczba produktów, wartość koszyka). Te dane biorą się ze
 * standaryzowanego obiektu `cart`, który `event.promise` również już zwraca (obok `detail`) —
 * `cart.totalQuantity` i `cart.cost.totalAmount.{amount,currencyCode}` — więc, tak jak wyżej,
 * bez żadnego dodatkowego zapytania sieciowego. `cart.cost.totalAmount.amount` przychodzi jako
 * string w jednostkach głównych waluty (np. "1100.00", NIE w groszach), stąd formatowanie przez
 * Intl.NumberFormat zamiast dzielenia przez 100 (dzielenie przez 100 jest natomiast potrzebne
 * dla cen pozycji z `detail.items`, bo te wciąż są w formacie AJAX Cart API, czyli w groszach —
 * patrz #formatMoney i jego wywołania).
 *
 * 17 września 2026 (wieczorem, na życzenie Daniela: "Jak dodajemy produkt z up-sell w koszyku
 * to nie wyświetlajmy juz tego potwierdzenia pop-up bo jest to totalnie bez sensu"): gdy
 * "Dobierz do zestawu" (assets/cart-addon-widget.js) jest używany z WNĘTRZA już otwartego
 * koszyka (koszyk pełnoekranowy lub strona /cart — widget tam domyślnie ma
 * `data-widget-context="cart"`), pokazywanie tego popupu nad już widocznym koszykiem nie ma
 * sensu — klient i tak patrzy na koszyk. Widget oznacza taki dodatek flagą
 * `detail.fondoriSuppressPopup: true`, którą #handleCartLinesUpdate sprawdza poniżej i po
 * prostu nie pokazuje popupu, nie przerywając przy tym niczego innego (CartLinesUpdateEvent
 * nadal jest wysyłane normalnie, więc lista produktów/suma/licznik w nagłówku odświeżają się
 * jak zawsze — to component-cart-items.js, nie ten plik). Na stronie produktowej
 * (`data-widget-context="product"`) ta flaga jest `false` i popup działa tak jak dotychczas
 * (Daniel wyraźnie o to prosił 16 września 2026).
 *
 * @extends {Component}
 */
class CartAddedPopupComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;

    // Same detection as assets/cart-drawer.js: when the add originates inside an open
    // MODAL <dialog> (e.g. quick-add), wait for that dialog's native close before showing
    // our popup, so focus restoration on the source dialog runs first.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    const lines = event.lines;

    event.promise
      ?.then(({ detail, cart }) => {
        if (detail?.didError) return;
        // Fondori — dodano z widgetu "Dobierz do zestawu" wewnątrz już otwartego koszyka:
        // klient i tak patrzy na koszyk, więc ten popup byłby zbędny. Zob. komentarz klasy.
        if (detail?.fondoriSuppressPopup) return;

        const addedItem = this.#findAddedItem(lines, detail?.items);
        const extraLines = Array.isArray(lines) ? lines.length - 1 : 0;

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', () => this.#show(addedItem, extraLines, cart), { once: true });
        } else {
          this.#show(addedItem, extraLines, cart);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-added-popup] Event promise rejected:', error);
      });
  };

  /**
   * Finds the cart item (AJAX-shaped: product_title, image, variant_title, variant_id, price,
   * final_line_price, ...) matching the first just-added line, so the popup can display what was
   * actually added. `merchandiseId` may arrive as a plain numeric id or a
   * `gid://shopify/ProductVariant/<id>` — both are handled by matching on the trailing digits.
   * @param {{merchandiseId: string | number, quantity: number}[] | undefined} lines
   * @param {any[] | undefined} cartItems
   * @returns {(any & { addedQuantity: number }) | null}
   */
  #findAddedItem(lines, cartItems) {
    const line = lines?.[0];
    if (!line || !Array.isArray(cartItems)) return null;

    const variantId = String(line.merchandiseId).match(/(\d+)\s*$/)?.[1];
    if (!variantId) return null;

    const item = cartItems.find((cartItem) => String(cartItem?.variant_id) === variantId);
    if (!item) return null;

    return { ...item, addedQuantity: line.quantity || 1 };
  }

  /**
   * Resizes a Shopify CDN image URL by inserting a `_{size}x` suffix before the file extension
   * (the standard Shopify image transform convention), so the popup doesn't load a full-size
   * product photo for a thumbnail.
   * @param {string} url
   * @param {number} size
   * @returns {string}
   */
  #resizeImage(url, size) {
    return url.replace(/(\.[a-zA-Z0-9]+)(\?.*)?$/, (_match, ext, query) => `_${size}x${ext}${query || ''}`);
  }

  /**
   * Maps the storefront language to an Intl locale for money formatting, so amounts read
   * naturally in each of the store's three languages (e.g. "1 100,00 zł" vs "zł1,100.00").
   * @returns {string}
   */
  #getLocale() {
    switch (this.dataset.lang) {
      case 'de':
        return 'de-DE';
      case 'en':
        return 'en-GB';
      default:
        return 'pl-PL';
    }
  }

  /**
   * Formats a money amount (in major currency units, e.g. zł/€/£, NOT cents) using Intl, so it
   * reads correctly for any currency the store trades in without needing the shop's money_format
   * setting. Callers passing AJAX Cart API prices (which are in cents) must divide by 100 first —
   * see the two call sites below.
   * @param {number} amount
   * @param {string | undefined} currencyCode
   * @returns {string}
   */
  #formatMoney(amount, currencyCode) {
    if (!Number.isFinite(amount) || !currencyCode) return '';
    try {
      return new Intl.NumberFormat(this.#getLocale(), { style: 'currency', currency: currencyCode }).format(amount);
    } catch {
      return `${amount} ${currencyCode}`;
    }
  }

  /**
   * @param {(any & { addedQuantity: number }) | null} item
   * @param {number} extraLines Number of additional distinct lines added alongside `item` (batch adds).
   * @param {string | undefined} currencyCode
   */
  #populateProduct(item, extraLines, currencyCode) {
    const { productImage, productTitle, productVariant, productPrice, productQuantity, productRow } = this.refs;

    if (!productRow) return;

    if (!item) {
      productRow.hidden = true;
      return;
    }

    productRow.hidden = false;

    if (productImage) {
      if (item.image) {
        productImage.src = this.#resizeImage(item.image, 240);
        productImage.alt = item.product_title || '';
        productImage.hidden = false;
      } else {
        productImage.hidden = true;
      }
    }

    if (productTitle) {
      productTitle.textContent = item.product_title || '';
    }

    if (productVariant) {
      const showVariant = !!item.variant_title && item.variant_title !== 'Default Title';
      productVariant.textContent = showVariant ? item.variant_title : '';
      productVariant.hidden = !showVariant;
    }

    if (productPrice) {
      // AJAX Cart API prices (final_line_price / price) are in cents — divide by 100 to get
      // major currency units before handing off to #formatMoney. See the class doc comment.
      const cents = item.final_line_price ?? item.price ?? null;
      if (typeof cents === 'number' && currencyCode) {
        const formatted = this.#formatMoney(cents / 100, currencyCode);
        productPrice.textContent = formatted;
        productPrice.hidden = !formatted;
      } else {
        productPrice.hidden = true;
      }
    }

    if (productQuantity) {
      const parts = [];
      if (item.addedQuantity > 1) parts.push(`× ${item.addedQuantity}`);
      if (extraLines > 0) {
        const template = this.dataset.extraItemsText || '';
        parts.push(template.replace('{{ count }}', String(extraLines)));
      }
      const text = parts.join(' · ');
      productQuantity.textContent = text;
      productQuantity.hidden = !text;
    }
  }

  /**
   * Populates the cart-wide summary (item count + cart value) from the standardized `cart`
   * object that `event.promise` resolves alongside `detail`. `cart.cost.totalAmount.amount` is
   * already in major currency units (a decimal string like "1100.00"), unlike the AJAX item
   * prices used in #populateProduct above.
   * @param {{ totalQuantity?: number, cost?: { totalAmount?: { amount?: string, currencyCode?: string } } } | null | undefined} cart
   */
  #populateSummary(cart) {
    const { summaryCount, summaryTotal } = this.refs;
    const totalQuantity = cart?.totalQuantity;
    const currencyCode = cart?.cost?.totalAmount?.currencyCode;
    const amount = cart?.cost?.totalAmount?.amount;

    if (summaryCount) {
      if (typeof totalQuantity === 'number') {
        const template = this.dataset.itemsInCartText || '';
        summaryCount.textContent = template.replace('{{ count }}', String(totalQuantity));
        summaryCount.hidden = false;
      } else {
        summaryCount.hidden = true;
      }
    }

    if (summaryTotal) {
      const parsedAmount = amount != null ? Number(amount) : NaN;
      summaryTotal.textContent = currencyCode ? this.#formatMoney(parsedAmount, currencyCode) : '';
    }
  }

  /**
   * @param {(any & { addedQuantity: number }) | null} item
   * @param {number} extraLines
   * @param {{ totalQuantity?: number, cost?: { totalAmount?: { amount?: string, currencyCode?: string } } } | null | undefined} cart
   */
  #show(item, extraLines = 0, cart) {
    if (this.refs.dialog.open) return;
    this.#populateProduct(item, extraLines, cart?.cost?.totalAmount?.currencyCode);
    this.#populateSummary(cart);
    this.refs.dialog.showModal();
  }

  /**
   * "Przejdź do koszyka" — closes this popup and opens the cart drawer.
   */
  goToCart() {
    this.refs.dialog.close();
    const cartDrawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    cartDrawer?.open?.();
  }

  /**
   * "Kontynuuj zakupy" (i przycisk zamknięcia ×) — po prostu zamyka popup, bez nawigacji.
   */
  continueShopping() {
    this.refs.dialog.close();
  }
}

if (!customElements.get('cart-added-popup-component')) {
  customElements.define('cart-added-popup-component', CartAddedPopupComponent);
}
